import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, ClipboardList, CheckCircle, Clock, Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';

const ReportsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const isCoach = user?.role === 'coach';

  useEffect(() => {
    apiFetch('/api/reports')
      .then((r) => r.ok ? r.json() : [])
      .then(setReports)
      .catch(() => setReports([]))
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) return <div className="loading"><div className="loading-spinner" /></div>;

  return (
    <div className="page-container animate-fade-in">
      <div className="header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            className="btn btn-icon btn-secondary"
            onClick={() => navigate(-1)}
            style={{ flexShrink: 0 }}
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <p className="text-muted" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>DragonFit</p>
            <h1 className="header-title">{t('reports.inbox_title')}</h1>
          </div>
        </div>
        <img src="/dragon-logo.png" alt="DragonFit" className="header-logo" />
      </div>

      {reports.length === 0 ? (
        <div className="empty-state">
          <ClipboardList className="empty-icon" />
          <h3 className="empty-title">{t('reports.empty_title')}</h3>
          <p className="empty-desc">
            {isCoach ? t('reports.empty_coach_desc') : t('reports.empty_athlete_desc')}
          </p>
          {!isCoach && (
            <button className="btn btn-primary" onClick={() => navigate('/reports/new')}>
              <Plus size={18} />
              {t('reports.send_btn')}
            </button>
          )}
        </div>
      ) : (
        reports.map((report) => {
          const hasFeedback = !!report.feedback;
          const isUnread = isCoach
            ? !report.read_by_coach
            : hasFeedback && !report.read_by_athlete;

          return (
            <div
              key={report.report_id}
              className="card mb-3"
              style={{
                cursor: 'pointer',
                position: 'relative',
                borderColor: isUnread ? 'rgba(var(--primary-rgb),0.5)' : undefined,
              }}
              onClick={() => navigate(`/reports/${report.report_id}`)}
            >
              {isUnread && (
                <span style={{
                  position: 'absolute', top: '14px', right: '14px',
                  width: '10px', height: '10px', borderRadius: '50%',
                  background: 'var(--primary)', boxShadow: '0 0 8px rgba(var(--primary-rgb),0.7)',
                }} />
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                  {isCoach && (
                    <div style={{
                      width: '42px', height: '42px', borderRadius: '50%',
                      background: 'rgba(var(--primary-rgb),0.15)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, fontSize: '18px',
                    }}>
                      {report.athlete_picture
                        ? <img src={report.athlete_picture} alt="" style={{ width: '42px', height: '42px', borderRadius: '50%', objectFit: 'cover' }} />
                        : '🐉'
                      }
                    </div>
                  )}
                  <div style={{ minWidth: 0 }}>
                    {isCoach && (
                      <p style={{ fontWeight: '600', color: 'var(--foreground)', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {report.athlete_name}
                      </p>
                    )}
                    <p style={{ fontSize: '13px', color: 'var(--muted-foreground)', marginBottom: '4px' }}>
                      {formatDate(report.created_at)}
                      {report.weight ? ` · ${report.weight} kg` : ''}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {hasFeedback ? (
                        <span style={{ fontSize: '12px', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '600' }}>
                          <CheckCircle size={12} /> {t('reports.status_reviewed')}
                        </span>
                      ) : (
                        <span style={{ fontSize: '12px', color: 'var(--muted-foreground)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Clock size={12} /> {t('reports.status_pending')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <ChevronRight size={18} style={{ color: 'var(--muted-foreground)', flexShrink: 0, marginLeft: '8px' }} />
              </div>

              {report.notes && (
                <p style={{
                  fontSize: '13px', color: 'var(--muted-foreground)',
                  marginTop: '10px', paddingTop: '10px',
                  borderTop: '1px solid var(--border)',
                  overflow: 'hidden', display: '-webkit-box',
                  WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                }}>
                  {report.notes}
                </p>
              )}
            </div>
          );
        })
      )}

      {/* FAB para que el atleta envíe un nuevo reporte */}
      {!isCoach && (
        <button className="fab" onClick={() => navigate('/reports/new')} title={t('reports.send_title')}>
          <Plus size={24} />
        </button>
      )}
    </div>
  );
};

export default ReportsPage;
